import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { ShippingZoneUpdateInput } from "@/modules/shipping/domain/contracts";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircleIcon } from "lucide-react";
import { useUpdateZone, useZoneQuery } from "../api/queries";
import { ZoneFormTour, useZoneFormTour } from "../tour";
import { ZoneForm } from "../ui/ZoneForm";

export function ZoneEditPage() {
  const nav = useNavigate({ from: "/app/shipping/zones/$id/edit" });
  const { id } = useParams({ from: "/app/shipping/zones/$id/edit" });
  const { data, ...result } = useZoneQuery(id);
  const updateZone = useUpdateZone(id);
  const { run, handleJoyrideCallback, startTour } = useZoneFormTour();
  const zone = data;
  const zoneName =
    zone && typeof zone.name === "object" && zone.name !== null
      ? ((zone.name as { lo?: string })?.lo ??
        (zone.name as { en?: string })?.en ??
        zone.slug)
      : ((zone as { name?: string; slug?: string })?.name ??
        (zone as { slug?: string })?.slug ??
        id ??
        "ແກ້ໄຂ");

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ໂຊນ", to: "/app/shipping/zones" },
            { label: zoneName },
          ]}
        />
      </Header>
      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ແກ້ໄຂໂຊນ{" "}
              <Button
                variant="ghost"
                size="icon"
                onClick={startTour}
                className="ml-2 size-6"
                title="ເລີ່ມທົດລອງການນຳທາງ"
              >
                <HelpCircleIcon className="h-4 w-4" />
              </Button>
            </h2>
            <p className="text-muted-foreground">ປັບລາຍລະອຽດຂອງໂຊນ.</p>
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/shipping/zones" } as any)}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>
        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດໂຊນ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!zone ? null : (
            <div className="mt-6 rounded-xl border bg-card p-6">
              <ZoneForm
                initialValues={{
                  code: zone.code,
                  nameLo: (zone.name as { lo?: string })?.lo ?? "",
                  nameTh: (zone.name as { th?: string })?.th ?? "",
                  nameEn: (zone.name as { en?: string })?.en ?? "",
                  sortOrder: zone.sortOrder,
                }}
                onSubmit={async (values: ShippingZoneUpdateInput) => {
                  await updateZone.mutateAsync(values);
                  nav({ to: "/app/shipping/zones" } as any);
                }}
                onCancel={() => nav({ to: "/app/shipping/zones" } as any)}
                submitting={updateZone.isPending}
              />
            </div>
          )}
        </QueryState>
        <ZoneFormTour run={run} onCallback={handleJoyrideCallback} />
      </Main>
    </>
  );
}
